import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import {ExcludeCondition} from 'graphql-client/declarative-translations/RESTExcludeConditions';

import BaseFieldTranslator from './BaseFieldTranslator';
import {TranslatableValue} from './DeclarativeTranslator';
import {PossibleTypesMapping} from './PossibleTypesMapping';
import {GraphQLSelectionSet, RESTField} from './preprocessedData';
import translateListOrObject from './translateListOrObject';
import {BaseTypenameResolver, getTypenameResolver} from './TypenameResolver';

function getNestedFieldNames(
  graphQLSelectionSet: GraphQLSelectionSet,
  fieldName: string
): Array<string> | null {
  const selectionSet = graphQLSelectionSet.fieldsByName[fieldName].selectionSet;
  if (!selectionSet) {
    // If selection_set is None, we probably don't want to parse this field, e.g. may be destined
    // for the SimpleNameFieldTranslator
    return null;
  }

  // TODO: this will hard error when we add inline fragment spreads, as we are assuming that the
  // selection is a field
  selectionSet.selections.forEach((selection) => {
    assert(
      selection.kind === 'Field',
      `IdentityFieldTranslator expected only fields when processing selection set for field=${fieldName}, but instead encountered ${selection.kind}.\n` +
        'This usually means a custom fieldTranslator is needed to process the more complex inline fragments.'
    );
  });

  // @ts-expect-error
  return selectionSet.selections.map((selection) => selection.name.value);
}

/**
 * Translator that handles identifier fields to their identity counterpart.
 *
 * Example:
 *    - xId <-> x { id }
 */
export default class IdentityFieldTranslator extends BaseFieldTranslator {
  typenameResolver: BaseTypenameResolver;

  constructor(
    restKey: string,
    graphQLKey: string,
    excludeCondition: ExcludeCondition | null,
    typenameResolver: BaseTypenameResolver
  ) {
    super(restKey, graphQLKey, excludeCondition);
    this.typenameResolver = typenameResolver;
  }

  public static tryParse(
    declarationName: string,
    parsedRESTField: RESTField,
    graphQLSelectionSet: GraphQLSelectionSet,
    possibleTypesMapping: PossibleTypesMapping
  ): BaseFieldTranslator | null {
    const fieldName = parsedRESTField.fieldName;

    let gqlField = parsedRESTField.customGraphQLName;

    if (!gqlField) {
      // Optimistically attempt translation: xId -> x { id }
      const match = fieldName.match(/(.*)Id/);
      if (match && graphQLSelectionSet.fieldsByName[match[1]] != null) {
        gqlField = match[1];
      }
    }

    if (gqlField) {
      const nestedFieldNames = getNestedFieldNames(graphQLSelectionSet, gqlField);
      // Note: This is different from the BE because the BE automatically inserts the __typename field when
      // running tests, whereas we don't (Apollo will handle that when it queries the server).
      if (isEqual(nestedFieldNames, ['id'])) {
        return new IdentityFieldTranslator(
          fieldName,
          gqlField,
          parsedRESTField.getExcludeCondition(),
          getTypenameResolver(parsedRESTField.typenameResolverName)
        );
      }
    }

    return null;
  }

  public getGQLValue(restObj: TranslatableValue): unknown {
    const restValue = restObj[this.restKey];
    return translateListOrObject(restValue, (id: string) => ({
      __typename: this.typenameResolver.getTypenameFromID(id),
      id,
    }));
  }

  public getRESTValue(gqlObj: TranslatableValue): unknown {
    const gqlValue = gqlObj[this.graphQLKey];
    return translateListOrObject(gqlValue, (value: {__typename: string; id: string}) => value.id);
  }
}
